import { useState, useCallback, useEffect } from 'react';

/**
 * Positions a portal dropdown menu under a trigger element and keeps it
 * aligned on scroll/resize. Use with createPortal(..., document.body).
 */
export function usePortalMenuPosition(isOpen, triggerRef) {
  const [menuStyle, setMenuStyle] = useState({});

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef?.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'absolute',
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: Math.max(rect.width, 220),
      zIndex: 10050,
    });
  }, [triggerRef]);

  useEffect(() => {
    if (!isOpen) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [isOpen, updateMenuPosition]);

  return { menuStyle, updateMenuPosition };
}

/**
 * Closes when mousedown is outside both trigger and portal menu.
 */
export function useOutsideClose(isOpen, setIsOpen, triggerRef, menuRef, onClose) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(event.target);
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        if (onClose) onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setIsOpen, triggerRef, menuRef, onClose]);
}
